/**
 * English translations for Onboarding System
 * User onboarding checklist and progress tracking
 */

export const onboarding = {
  // Checklist UI
  'onboarding.checklist.title': 'Onboarding Checklist',
  'onboarding.checklist.description': 'Complete these steps to get the most out of subvention360',
  'onboarding.checklist.progress': '{completed} of {total} completed',
  'onboarding.checklist.completed': 'Congratulations! You\'ve completed onboarding',

  // Steps
  'onboarding.steps.create_profile': 'Create your first profile',
  'onboarding.steps.add_company_data': 'Add your company information',
  'onboarding.steps.explore_subsidies': 'Explore available subsidies',
  'onboarding.steps.save_favorite': 'Save your first favorite subsidy',
  'onboarding.steps.setup_calendar': 'Set up calendar reminders',

  // Actions
  'onboarding.action.start': 'Start',
  'onboarding.action.continue': 'Continue',
  'onboarding.action.skip': 'Skip',
  'onboarding.action.complete': 'Complete',

  // Messages
  'onboarding.welcome.title': 'Welcome to subvention360!',
  'onboarding.welcome.subtitle': 'Let\'s start your journey to finding subsidies',
  'onboarding.completion.title': 'Congratulations!',
  'onboarding.completion.message': 'You\'re now ready to find the best subsidies for your business',

  // Role Selection
  'onboarding.role_selection.roles.consultant.title': 'Consultant',
  'onboarding.role_selection.roles.business_owner.title': 'Business Owner',
  'onboarding.role_selection.roles.team_member.title': 'Team Member',

  // Wizard Welcome Messages (Success Toast after onboarding completion)
  'onboarding.wizard.welcome.consultant': 'Your consultant account is ready! You can now manage multiple clients and find subsidies for them.',
  'onboarding.wizard.welcome.business_owner': 'Your account is set up! Discover subsidies tailored to your business.',
  'onboarding.wizard.welcome.team_member': 'Welcome to the team! You can now collaborate on subsidies.',

  // Wizard Step Titles & Descriptions
  'onboarding.wizard.step1.title': 'Your Business Details',
  'onboarding.wizard.step1.subtitle': 'Tell us about your business',
  'onboarding.wizard.step1.subtitle.simple': 'Essential information',
  'onboarding.wizard.step1.subtitle.long': 'This information helps us match you with the most relevant funding opportunities',
  'onboarding.wizard.step2.title': 'Privacy & Preferences',
  'onboarding.wizard.step2.subtitle': 'Privacy settings',
  'onboarding.wizard.step3.title': 'Payment Method',
  'onboarding.wizard.step3.subtitle': 'No payment today',

  // Full Name Field
  'onboarding.wizard.fullName': 'Full Name',
  'onboarding.wizard.fullName.placeholder': 'Your first and last name',

  // Business Details Form
  'onboarding.business.companyName': 'Company Name',
  'onboarding.business.companyName.placeholder': 'Enter your company name',
  'onboarding.business.siret': 'SIRET Number',
  'onboarding.business.siret.placeholder': '14-digit SIRET (e.g., 53801120600017)',
  'onboarding.business.siret.label': 'SIRET Number',
  'onboarding.business.siret.helper': 'French company ID',
  'onboarding.business.siret.readonly': 'Auto-filled from company search above, or enter manually',
  'onboarding.business.siret.warning': '⚠️ Required for automatic company data enrichment and accurate subsidy matching',
  'onboarding.business.industry': 'Industry',
  'onboarding.business.industry.placeholder': 'Select your industry',
  'onboarding.business.companySize': 'Company Size',
  'onboarding.business.companySize.placeholder': 'Select company size',
  'onboarding.business.location': 'Headquarters Location',
  'onboarding.business.location.placeholder': 'City, Country',
  'onboarding.business.operatingRegions': 'Operating Regions',
  'onboarding.business.operatingRegions.placeholder': 'Where do you operate?',
  'onboarding.business.fundingInterests': 'Funding Interests (select all that apply)',

  // Company Sizes
  'onboarding.business.size.micro': '1-9 employees (Micro)',
  'onboarding.business.size.small': '10-49 employees (Small)',
  'onboarding.business.size.medium': '50-249 employees (Medium)',
  'onboarding.business.size.large': '250+ employees (Large)',

  // Industries
  'onboarding.business.industry.agriculture': 'Agriculture & Food',
  'onboarding.business.industry.technology': 'Technology & IT',
  'onboarding.business.industry.manufacturing': 'Manufacturing',
  'onboarding.business.industry.energy': 'Renewable Energy',
  'onboarding.business.industry.healthcare': 'Healthcare',
  'onboarding.business.industry.education': 'Education',
  'onboarding.business.industry.tourism': 'Tourism & Hospitality',
  'onboarding.business.industry.transportation': 'Transportation',
  'onboarding.business.industry.construction': 'Construction',
  'onboarding.business.industry.services': 'Professional Services',
  'onboarding.business.industry.retail': 'Retail & E-commerce',
  'onboarding.business.industry.other': 'Other',

  // Funding Interests
  'onboarding.business.funding.rd': 'R&D Grants',
  'onboarding.business.funding.innovation': 'Innovation Funding',
  'onboarding.business.funding.export': 'Export Support',
  'onboarding.business.funding.digital': 'Digital Transformation',
  'onboarding.business.funding.green': 'Green Transition',
  'onboarding.business.funding.training': 'Training & Skills',
  'onboarding.business.funding.tax': 'Tax Relief',
  'onboarding.business.funding.loans': 'Low-interest Loans',

  // Privacy & Consent
  'onboarding.consent.title': 'Privacy & Communication Preferences',
  'onboarding.consent.subtitle': 'Choose how you want to use subvention360 and stay informed',
  'onboarding.consent.gdpr': 'Privacy Policy & Terms',
  'onboarding.consent.gdpr.text': 'I accept the {privacy} and {terms} of subvention360. Your data will be processed securely and used only to provide our services.',
  'onboarding.consent.gdpr.short': 'I accept the Privacy Policy and Terms of Use. Your data will be processed securely.',
  'onboarding.consent.security': 'Your data is secure and will only be used to improve your subsidy recommendations.',
  'onboarding.consent.gdpr.accept': 'I accept the',
  'onboarding.consent.gdpr.privacy': 'Privacy Policy',
  'onboarding.consent.gdpr.terms': 'Terms of Use',
  'onboarding.consent.gdpr.and': 'and the',
  'onboarding.consent.gdpr.cgv': 'Terms of Sale',
  'onboarding.consent.gdpr.dataInfo': 'Your data will be processed securely and used only to provide our services.',
  'onboarding.consent.preferences.title': 'Communication Preferences',
  'onboarding.consent.updates': 'Platform updates',
  'onboarding.consent.updates.desc': 'Receive information about new features, improvements and important announcements',
  'onboarding.consent.alerts': 'Subsidy alerts (Recommended)',
  'onboarding.consent.alerts.desc': 'Be notified of new aids that match your profile and interests',
  'onboarding.consent.tips': 'Tips & Best Practices',
  'onboarding.consent.tips.desc': 'Receive useful advice to improve your applications and success rate',
  'onboarding.consent.footer': 'You can modify these preferences at any time in your account settings. We respect your privacy and will never share your data with third parties.',

  // Navigation
  'onboarding.wizard.previous': 'Previous',
  'onboarding.wizard.next': 'Next',
  'onboarding.wizard.finish': 'Finish',
  'onboarding.wizard.configuring': 'Configuring...',
  'onboarding.wizard.progress': 'Step {current} of {total} • Almost done!',

  // Errors
  'onboarding.error.required': 'Required fields',
  'onboarding.error.required.desc': 'Please fill in all required fields (*)',
  'onboarding.error.siret.invalid': 'Invalid SIRET',
  'onboarding.error.siret.invalid.desc': 'SIRET number must contain exactly 14 digits.',
  'onboarding.error.consent.required': 'Acceptance required',
  'onboarding.error.consent.required.desc': 'You must accept the Privacy Policy and Terms of Use to continue.',
} as const;
